import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store';
import { Card, Button } from '@tadagram/ui';
import {
  Activity, Users, TrendingUp, AlertCircle,
  CheckCircle2, XCircle, Clock, BarChart3, ChevronRight,
  RefreshCw, AlertTriangle, Download,
} from 'lucide-react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import apiService from '../services/api';

// ---------- Types ----------
interface ActivityOverview {
  total_executions: number;
  completed_runs: number;
  failed_runs: number;
  total_tasks_done: number;
  total_tasks_failed: number;
  unique_accounts: number;
  avg_duration_seconds: number;
  success_rate: number;
}
interface DailyActivity {
  date: string;
  total_runs: number;
  completed_runs: number;
  failed_runs: number;
  tasks_done: number;
  tasks_failed: number;
}
interface AccountActivity {
  account_id: string;
  total_tasks: number;
  completed: number;
  failed: number;
  by_task_type: Record<string, number>;
  last_active_at?: string;
}
interface TaskTypeActivity {
  task_type: string;
  total: number;
  completed: number;
  failed: number;
  success_rate: number;
}
interface ActivitySummary {
  period: { type: string; start_date: string; end_date: string };
  overview: ActivityOverview;
  by_date: DailyActivity[];
  by_account: AccountActivity[];
  by_task_type: TaskTypeActivity[];
}
interface TaskCount { completed: number; failed: number; }
interface AccountDailyStat {
  worker_uuid: string;
  account_id: string;
  date: string;
  tasks: Record<string, TaskCount>;
  total_completed: number;
  total_failed: number;
  last_run_at?: string;
}

// ---------- Labels ----------
const TASK_LABEL_KEYS = [
  'post_to_group', 'post_to_profile', 'comment_on_post', 'like_posts',
  'join_groups', 'search_groups', 'find_join_groups', 'like_pages',
  'share_url', 'interact', 'check_message', 'reply_to_comments',
  'view_notifications', 'accept_friend_requests', 'search_add_friend',
  'find_trend_fb', 'find_group_posts', 'ai_generate_asset',
  'seeding_contents_manager', 'account_selector', 'filter_post', 'miscellaneous',
  'like_post_instagram', 'repost_post_instagram', 'comment_post_instagram',
] as const;

function getTaskLabel(t: TFunction, key: string): string {
  const val = t(`taskLabels.${key}`, '');
  return val || key;
}

const TASK_ICONS: Record<string, React.ReactNode> = {
  account_selector: <Users size={14} />,
  comment_on_post: <Activity size={14} />,
  filter_post: <BarChart3 size={14} />,
  find_group_posts: <Activity size={14} />,
  post_to_group: <TrendingUp size={14} />,
  post_to_profile: <TrendingUp size={14} />,
  reply_to_comments: <Activity size={14} />,
  share_url: <TrendingUp size={14} />,
  like_posts: <CheckCircle2 size={14} />,
  like_pages: <CheckCircle2 size={14} />,
  like_post_instagram: <CheckCircle2 size={14} />,
  repost_post_instagram: <TrendingUp size={14} />,
  comment_post_instagram: <Activity size={14} />,
  miscellaneous: <Activity size={14} />,
};

type Period = 'day' | 'week' | 'month';
const PERIOD_KEYS: Period[] = ['day', 'week', 'month'];
const PERIOD_I18N: Record<Period, string> = { day: 'periodToday', week: 'periodWeek', month: 'periodMonth' };

// ---------- Health Gauge ----------
const HealthGauge: React.FC<{ score: number; t: TFunction }> = ({ score, t }) => {
  const r = 54, c = 2 * Math.PI * r;
  const dash = (score / 100) * c;
  const color = score >= 90 ? '#22c55e' : score >= 70 ? '#2AABEE' : score >= 50 ? '#f59e0b' : '#ef4444';
  const label = score >= 90 ? t('healthExcellent') : score >= 70 ? t('healthStable') : score >= 50 ? t('healthAttention') : t('healthCritical');
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-[140px] h-[140px]">
        <svg viewBox="0 0 128 128" className="w-full h-full -rotate-90">
          <circle cx="64" cy="64" r={r} fill="none" stroke="#e5e7eb" strokeWidth="8" />
          <circle cx="64" cy="64" r={r} fill="none" stroke={color} strokeWidth="8"
            strokeLinecap="round" strokeDasharray={`${dash} ${c}`}
            className="transition-all duration-1000 ease-out" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold" style={{ color }}>{score.toFixed(1)}<span className="text-base align-top ml-0.5">%</span></span>
          <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wider">{t('healthScore')}</span>
        </div>
      </div>
      <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-100 border border-gray-200">
        <span className="text-[11px] font-semibold text-text-primary uppercase tracking-wide">{t('status')}</span>
        <span className="text-[11px] font-semibold" style={{ color }}>{label}</span>
        {score >= 70 ? <CheckCircle2 size={13} className="text-green-500" /> : <AlertTriangle size={13} className="text-amber-500" />}
      </div>
    </div>
  );
};

// ---------- Stat Card ----------
const StatCard: React.FC<{
  icon: React.ReactNode; iconBg: string;
  label: string; value: string | number;
  sub?: React.ReactNode; subColor?: string;
}> = ({ icon, iconBg, label, value, sub, subColor }) => (
  <div className="bg-white border border-gray-100 rounded-xl p-4 hover:shadow-card-hover transition-shadow">
    <div className="flex items-center justify-between mb-3">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconBg}`}>{icon}</div>
      {sub && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${subColor || 'text-text-secondary bg-gray-50'}`}>{sub}</span>}
    </div>
    <p className="text-[11px] text-text-secondary uppercase tracking-wide font-medium mb-0.5">{label}</p>
    <p className="text-2xl font-bold text-text-primary tracking-tight">{value}</p>
  </div>
);

// ---------- Helpers ----------
function timeAgo(iso: string, t: TFunction): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return t('justNow');
  if (mins < 60) return t('activeMinAgo', { min: mins });
  const h = Math.floor(mins / 60);
  if (h < 24) return t('activeHourAgo', { hour: h });
  return t('activeDayAgo', { day: Math.floor(h / 24) });
}

function fmtDateLabel(d: string): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const [, m, day] = d.split('-');
  return `${months[parseInt(m) - 1]}${parseInt(day).toString().padStart(2, '0')}`;
}

// ==========================================================================
//  MAIN
// ==========================================================================
interface DashboardProps { activeWorkerId?: string | null; }

export const Dashboard: React.FC<DashboardProps> = ({ activeWorkerId }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const { workerInfo, seedingAccounts, loadWorkerInfo, loadSeedingAccounts, isLoading } = useAppStore();

  const [period, setPeriod] = useState<Period>('week');
  const [activity, setActivity] = useState<ActivitySummary | null>(null);
  const [accountStats, setAccountStats] = useState<AccountDailyStat[]>([]);
  const [actLoading, setActLoading] = useState(false);
  const [actError, setActError] = useState<string | null>(null);

  useEffect(() => {
    loadWorkerInfo(activeWorkerId || undefined);
    loadSeedingAccounts(true);
  }, [activeWorkerId, loadWorkerInfo, loadSeedingAccounts]);

  const loadActivity = useCallback(async () => {
    if (!activeWorkerId) return;
    setActLoading(true); setActError(null);
    try {
      const [res, statsRes] = await Promise.all([
        apiService.getDashboardActivity(activeWorkerId, period),
        apiService.getDashboardAccountStats(activeWorkerId, period),
      ]);
      if (res?.success) setActivity(res.data as ActivitySummary);
      if (statsRes?.success) setAccountStats((statsRes.data as AccountDailyStat[]) ?? []);
    } catch (e: any) {
      setActError(e?.message ?? t('loadError'));
    } finally { setActLoading(false); }
  }, [activeWorkerId, period]);

  useEffect(() => { loadActivity(); }, [loadActivity]);

  const workerAccounts = activeWorkerId ? seedingAccounts.filter(a => a.worker_uuid === activeWorkerId) : seedingAccounts;
  const activeAccounts = workerAccounts.filter(a => a.status === 'active').length;
  const accountById = new Map(workerAccounts.map(a => [a.id, a]));
  const pct = (n: number, d: number) => d > 0 ? Math.round((n / d) * 100) : 0;
  const fmtSec = (s: number) => s >= 60 ? `${Math.round(s / 60)}m ${Math.round(s % 60)}s` : `${Math.round(s)}s`;

  const ov = activity?.overview;
  const maxBarRuns = Math.max(1, ...(activity?.by_date.map(d => d.total_runs) ?? []));
  const maxBarTasks = Math.max(1, ...(activity?.by_date.map(d => d.tasks_done) ?? []));
  const maxBar = Math.max(maxBarRuns, maxBarTasks);
  const healthScore = ov ? Math.round(ov.success_rate * 1000) / 10 : 0;

  const periodLabel = useMemo(() => {
    if (!activity?.period) return '';
    const { start_date, end_date } = activity.period;
    const fmt = (d: string) => {
      const [y, m, day] = d.split('-');
      return t('periodRange_date', { day: parseInt(day), month: parseInt(m), year: y });
    };
    return t('periodRange', { from: fmt(start_date), to: fmt(end_date) });
  }, [activity?.period, t]);

  const groupedStats = useMemo(() => {
    const result: Record<string, { tasks: Record<string, { completed: number; failed: number }>; daily: AccountDailyStat[]; totalCompleted: number; totalFailed: number; lastRunAt?: string }> = {};
    for (const stat of accountStats) {
      if (!result[stat.account_id]) result[stat.account_id] = { tasks: {}, daily: [], totalCompleted: 0, totalFailed: 0 };
      const g = result[stat.account_id];
      g.daily.push(stat);
      g.totalCompleted += stat.total_completed;
      g.totalFailed += stat.total_failed;
      for (const [tt, c] of Object.entries(stat.tasks)) {
        if (!g.tasks[tt]) g.tasks[tt] = { completed: 0, failed: 0 };
        g.tasks[tt].completed += c.completed;
        g.tasks[tt].failed += c.failed;
      }
      if (stat.last_run_at && (!g.lastRunAt || stat.last_run_at > g.lastRunAt)) g.lastRunAt = stat.last_run_at;
    }
    return result;
  }, [accountStats]);

  // === Export CSV ===
  const handleExport = () => {
    if (!activity || !ov) return;
    const escCSV = (v: string) => { const e = v.replace(/"/g, '""'); return /[,"\n\r]/.test(v) ? `"${e}"` : e; };
    const rows = [
      ['Metric', 'Value'],
      ['Period', `${activity.period.start_date} to ${activity.period.end_date}`],
      ['Total Executions', String(ov.total_executions)],
      ['Completed Runs', String(ov.completed_runs)],
      ['Failed Runs', String(ov.failed_runs)],
      ['Tasks Done', String(ov.total_tasks_done)],
      ['Tasks Failed', String(ov.total_tasks_failed)],
      ['Accounts', String(ov.unique_accounts)],
      ['Success Rate', `${Math.round(ov.success_rate * 100)}%`],
      ['Avg Duration', fmtSec(ov.avg_duration_seconds)],
      [],
      ['Module', 'Total', 'Completed', 'Failed', 'Yield'],
      ...activity.by_task_type.map(tt => [
        getTaskLabel(t, tt.task_type),
        String(tt.total), String(tt.completed), String(tt.failed),
        `${Math.round(tt.success_rate * 100)}%`,
      ]),
    ];
    const csv = '\uFEFF' + rows.map(r => r.map(c => escCSV(c ?? '')).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dashboard_${period}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // =======================================================================
  //  RENDER
  // =======================================================================
  return (
    <div className="min-h-screen bg-[#f8f9fb]">
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-5">

        {/*  Header  */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-text-primary tracking-tight">{t('title')}</h1>
            {periodLabel && (
              <p className="text-xs text-text-secondary mt-1 flex items-center gap-1.5">
                <Clock size={12} className="opacity-50" />
                {periodLabel}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              {PERIOD_KEYS.map(p => (
                <Button key={p} variant={period === p ? 'secondary' : 'ghost'} size="xs"
                  onClick={() => setPeriod(p)}
                  className={period === p ? 'bg-white shadow-sm' : ''}>
                  {t(PERIOD_I18N[p])}
                </Button>
              ))}
            </div>
            <Button variant="secondary" size="sm" onClick={handleExport} disabled={!ov}
              icon={<Download size={13} />}>
              {t('export')}
            </Button>
            <Button variant="primary" size="sm" onClick={loadActivity} loading={actLoading}
              icon={<RefreshCw size={13} />}>
              {t('refresh')}
            </Button>
          </div>
        </div>

        {/*  No Worker  */}
        {!activeWorkerId && !isLoading && (
          <Card>
            <div className="text-center py-12 px-4">
              <AlertCircle className="mx-auto text-amber-400 mb-3" size={44} />
              <h3 className="font-semibold text-text-primary mb-1">{t('noWorkerYet')}</h3>
              <p className="text-sm text-text-secondary mb-4">{t('addFirstAccount')}</p>
              <Button onClick={() => navigate('/accounts')}>{t('addAccount')}</Button>
            </div>
          </Card>
        )}

        {/*  Loading  */}
        {actLoading && !activity && (
          <Card><div className="flex items-center justify-center py-16 gap-3 text-text-secondary">
            <div className="animate-spin h-5 w-5 border-2 border-tg-primary border-t-transparent rounded-full" />
            <span className="text-sm">{t('loading')}</span>
          </div></Card>
        )}

        {/*  Error  */}
        {actError && !activity && (
          <Card><div className="p-5 flex items-start gap-3">
            <AlertCircle size={18} className="text-red-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-600">{t('loadError')}</p>
              <p className="text-xs text-text-secondary mt-1">{actError}</p>
              <button onClick={loadActivity} className="text-xs text-tg-primary mt-2 font-medium hover:underline">{t('retry')}</button>
            </div>
          </div></Card>
        )}

        {ov && (<>

          {/*  KPI Cards  */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard icon={<Activity size={15} className="text-blue-500" />} iconBg="bg-blue-50" label={t('executions')} value={ov.total_executions}
              sub={<span className="text-green-600">+{pct(ov.completed_runs, ov.total_executions)}%</span>} subColor="bg-green-50" />
            <StatCard icon={<CheckCircle2 size={15} className="text-green-500" />} iconBg="bg-green-50" label={t('completed')} value={ov.completed_runs}
              sub={<><span className="text-green-600">{pct(ov.completed_runs, ov.total_executions)}%</span> {t('rate')}</>} subColor="bg-green-50 text-green-600" />
            <StatCard icon={<XCircle size={15} className="text-red-400" />} iconBg="bg-red-50" label={t('failed')} value={ov.failed_runs}
              sub={ov.failed_runs > 0 ? <span className="text-red-500"></span> : undefined} subColor="bg-red-50" />
            <StatCard icon={<Activity size={15} className="text-blue-500" />} iconBg="bg-blue-50" label={t('tasksDone')} value={ov.total_tasks_done} />
            <StatCard icon={<AlertTriangle size={15} className="text-amber-500" />} iconBg="bg-amber-50" label={t('taskFailures')} value={ov.total_tasks_failed}
              sub={ov.total_tasks_failed > 0 ? t('review') : undefined} subColor="bg-red-50 text-red-500" />
            <StatCard icon={<Users size={15} className="text-purple-500" />} iconBg="bg-purple-50" label={t('accounts')} value={ov.unique_accounts} />
          </div>

          {/*  Chart Row: Activity + Health  */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

            {/* Activity Velocity */}
            <div className="lg:col-span-2 bg-white border border-gray-100 rounded-xl p-5">
              <div className="flex items-center justify-between mb-1">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary">{t('activityVelocity')}</h3>
                  <p className="text-[11px] text-text-secondary">{t('activityVelocityDesc')}</p>
                </div>
                <div className="flex items-center gap-4 text-[11px] text-text-secondary">
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-tg-primary" /> {t('runs')}</span>
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-200" /> {t('tasks')}</span>
                </div>
              </div>

              {activity!.by_date.length > 0 ? (
                <div className="flex items-end gap-3 h-[180px] mt-4 px-2">
                  {activity!.by_date.map(d => {
                    const rH = Math.max(4, (d.total_runs / maxBar) * 150);
                    const tH = Math.max(4, (d.tasks_done / maxBar) * 150);
                    return (
                      <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5 group">
                        <div className="flex items-end gap-[4px] h-[150px]">
                          <div className="w-6 bg-tg-primary rounded-t-md transition-all duration-500 group-hover:opacity-80" style={{ height: `${rH}px` }}
                            title={t('runsTooltip', { count: d.total_runs })} />
                          <div className="w-6 bg-blue-200 rounded-t-md transition-all duration-500 group-hover:opacity-80" style={{ height: `${tH}px` }}
                            title={t('tasksTooltip', { count: d.tasks_done })} />
                        </div>
                        <span className="text-[10px] text-text-secondary font-medium uppercase">{fmtDateLabel(d.date)}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-[180px] flex items-center justify-center text-text-secondary text-sm">{t('noData')}</div>
              )}
            </div>

            {/* System Integrity */}
            <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] border border-gray-800 rounded-xl p-5 flex flex-col items-center justify-center text-center">
              <h3 className="text-sm font-semibold text-white/90 mb-0.5">{t('systemIntegrity')}</h3>
              <p className="text-[11px] text-white/50 mb-4">{period === 'day' ? t('trailing24h') : period === 'week' ? t('trailing7d') : t('trailing30d')}</p>
              <HealthGauge score={healthScore} t={t} />
            </div>
          </div>

          {/*  Account Portfolio + Module Yield  */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 min-w-0">

            {/* Account Portfolio */}
            <div className="bg-white border border-gray-100 rounded-xl">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-text-primary">{t('accountPortfolio')}</h3>
                <button onClick={() => navigate('/accounts')} className="text-[11px] font-semibold text-tg-primary hover:underline uppercase tracking-wide flex items-center gap-0.5">
                  {t('allAccounts')} <ChevronRight size={13} />
                </button>
              </div>
              <div className="divide-y divide-gray-50">
                {Object.keys(groupedStats).length > 0
                  ? Object.entries(groupedStats)
                    .sort(([, a], [, b]) => (b.totalCompleted + b.totalFailed) - (a.totalCompleted + a.totalFailed))
                    .map(([accountId, data]) => {
                      const info = accountById.get(accountId);
                      const name = info?.full_name || info?.username || accountId.slice(0, 11) + '';
                      const total = data.totalCompleted + data.totalFailed;
                      const rate = total > 0 ? pct(data.totalCompleted, total) : 0;
                      return (
                        <div key={accountId} className="px-5 py-4">
                          {/* Account header */}
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0"><Users size={15} className="text-text-secondary" /></div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-text-primary truncate">{name}</p>
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-50 text-green-600 border border-green-100">
                                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> {t('live')}
                                </span>
                              </div>
                              {data.lastRunAt && <p className="text-[10px] text-text-secondary">{timeAgo(data.lastRunAt, t)}</p>}
                            </div>
                            {/* Summary pills */}
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-600">
                                <CheckCircle2 size={12} /> {data.totalCompleted}
                              </span>
                              {data.totalFailed > 0 && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-500">
                                  <XCircle size={12} /> {data.totalFailed}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Integrity bar */}
                          <div className="mt-2.5">
                            <div className="flex items-center justify-between text-[10px] text-text-secondary mb-1">
                              <span className="uppercase tracking-wide font-medium">{t('integrity')}</span>
                              <span>{data.totalCompleted} / {total} {t('complete')} · <span className={rate >= 80 ? 'text-tg-primary font-semibold' : rate >= 50 ? 'text-amber-500 font-semibold' : 'text-red-500 font-semibold'}>{rate}%</span></span>
                            </div>
                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full transition-all duration-700 ${rate >= 80 ? 'bg-tg-primary' : rate >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                                style={{ width: `${rate}%` }} />
                            </div>
                          </div>

                          {/* Task breakdown — pill grid */}
                          {Object.keys(data.tasks).length > 0 && (() => {
                            const entries = Object.entries(data.tasks)
                              .sort(([, a], [, b]) => (b.completed + b.failed) - (a.completed + a.failed));
                            return (
                              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                                {entries.map(([taskType, counts]) => {
                                  const hasFail = counts.failed > 0;
                                  const allFail = counts.completed === 0 && hasFail;
                                  const label = getTaskLabel(t, taskType);
                                  const taskTotal = counts.completed + counts.failed;
                                  const tooltip = `${label}: ${t('tooltipTotal', { total: taskTotal, success: counts.completed, fail: counts.failed })}`;
                                  return (
                                    <div key={taskType} title={tooltip}
                                      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-[10px] cursor-default ${allFail ? 'bg-red-50 border-red-200'
                                        : hasFail ? 'bg-amber-50/60 border-amber-200'
                                          : 'bg-gray-50 border-gray-100'
                                        }`}>
                                      <span className="text-text-secondary shrink-0 [&>svg]:w-[12px] [&>svg]:h-[12px]">
                                        {TASK_ICONS[taskType] || <Activity size={12} />}
                                      </span>
                                      <span className="text-text-secondary truncate flex-1 min-w-0">{label}</span>
                                      <span className="font-bold text-green-600 shrink-0 tabular-nums">{counts.completed}</span>
                                      {hasFail && <span className="font-bold text-red-500 shrink-0 tabular-nums">{counts.failed}✕</span>}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}

                          {/* Detail link */}
                          <button
                            onClick={() => navigate(`/reports?account=${accountId}`)}
                            className="mt-3 w-full flex items-center justify-center gap-1 text-[11px] font-semibold text-tg-primary hover:bg-blue-50 rounded-lg py-1.5 transition-colors"
                          >
                            {t('viewReport')} <ChevronRight size={13} />
                          </button>
                        </div>
                      );
                    })
                  : activity!.by_account.length > 0
                    ? activity!.by_account.sort((a, b) => b.total_tasks - a.total_tasks).map(acc => {
                      const info = accountById.get(acc.account_id);
                      const name = info?.full_name || info?.username || acc.account_id.slice(0, 11) + '';
                      const rate = pct(acc.completed, acc.total_tasks);
                      return (
                        <div key={acc.account_id} className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0"><Users size={15} className="text-text-secondary" /></div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-text-primary truncate">{name}</p>
                              <p className="text-[10px] text-text-secondary">{acc.completed} {t('done')}{acc.failed > 0 ? ` · ${acc.failed} ${t('errors')}` : ''}</p>
                            </div>
                          </div>
                          <div className="mt-2.5 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-tg-primary rounded-full" style={{ width: `${rate}%` }} />
                          </div>
                          {/* Detail link */}
                          <button
                            onClick={() => navigate(`/reports?account=${acc.account_id}`)}
                            className="mt-3 w-full flex items-center justify-center gap-1 text-[11px] font-semibold text-tg-primary hover:bg-blue-50 rounded-lg py-1.5 transition-colors"
                          >
                            {t('viewReport')} <ChevronRight size={13} />
                          </button>
                        </div>
                      );
                    })
                    : <div className="px-5 py-10 text-center"><Users size={24} className="mx-auto text-text-secondary opacity-30 mb-2" /><p className="text-xs text-text-secondary">{t('noData')}</p></div>
                }
              </div>
            </div>

            {/* Module Yield Breakdown */}
            <div className="bg-white border border-gray-100 rounded-xl">
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-text-primary">{t('moduleYield')}</h3>
              </div>
              {activity!.by_task_type.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-50">
                        <th className="text-left px-3 sm:px-5 py-2.5 text-[10px] font-semibold text-text-secondary uppercase tracking-wider">{t('module')}</th>
                        <th className="text-right px-2 sm:px-3 py-2.5 text-[10px] font-semibold text-text-secondary uppercase tracking-wider whitespace-nowrap">{t('volume')}</th>
                        <th className="text-right px-2 sm:px-3 py-2.5 text-[10px] font-semibold text-text-secondary uppercase tracking-wider whitespace-nowrap">{t('success')}</th>
                        <th className="text-right px-2 sm:px-3 py-2.5 text-[10px] font-semibold text-text-secondary uppercase tracking-wider whitespace-nowrap">{t('failure')}</th>
                        <th className="text-right px-3 sm:px-5 py-2.5 text-[10px] font-semibold text-text-secondary uppercase tracking-wider whitespace-nowrap">{t('yield')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {activity!.by_task_type.sort((a, b) => b.total - a.total).map(tt => {
                        const y = Math.round(tt.success_rate * 100);
                        const yc = y >= 90 ? 'text-green-600' : y >= 70 ? 'text-amber-500' : 'text-red-500';
                        return (
                          <tr key={tt.task_type} className="hover:bg-gray-50/50 transition-colors">
                            <td className="px-3 sm:px-5 py-2.5">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center text-text-secondary shrink-0">
                                  {TASK_ICONS[tt.task_type] || <Activity size={14} />}
                                </span>
                                <span className="text-xs font-medium text-text-primary truncate max-w-[120px] sm:max-w-none">{getTaskLabel(t, tt.task_type)}</span>
                              </div>
                            </td>
                            <td className="text-right px-2 sm:px-3 py-2.5 text-xs text-text-secondary font-medium">{tt.total}</td>
                            <td className="text-right px-2 sm:px-3 py-2.5 text-xs text-green-600 font-bold">{tt.completed}</td>
                            <td className="text-right px-2 sm:px-3 py-2.5 text-xs text-red-500 font-medium">{tt.failed}</td>
                            <td className={`text-right px-3 sm:px-5 py-2.5 text-xs font-bold ${yc}`}>{y === 100 ? '100%' : `${y.toFixed(1)}%`}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-5 py-10 text-center"><BarChart3 size={24} className="mx-auto text-text-secondary opacity-30 mb-2" /><p className="text-xs text-text-secondary">{t('noData')}</p></div>
              )}
            </div>
          </div>

          {/*  Empty state  */}
          {ov.total_executions === 0 && (
            <Card><div className="text-center py-12 px-4">
              <Clock className="mx-auto text-text-secondary mb-3 opacity-40" size={40} />
              <p className="text-sm font-medium text-text-primary">{t('noActivityData')}</p>
              <p className="text-xs text-text-secondary mt-1">{t('dataAppearsAfterRun')}</p>
            </div></Card>
          )}

        </>)}
      </div>
    </div>
  );
};
